import React from 'react';
import { AccountUsageRow } from '../../types/dashboard';
import Avatar from './Avatar';
import RegionPill from './RegionPill';
import TrendLabel from './TrendLabel';
import './UsageTable.css';

interface UsageTableProps {
  rows: AccountUsageRow[];
}

const emailLabel = (row: AccountUsageRow): string => row.email ?? row.account_id;

// "Weekly / Till Date" for one metric, as a single compact cell — Queries
// (chats_started) has no trend of its own, so this is the shared,
// no-trend rendering; Messages adds its own TrendLabel alongside.
const MetricPair: React.FC<{ weekly: number; total: number }> = ({ weekly, total }) => (
  <>
    <span className="usage-table-usage-number">{weekly}</span>
    <span className="usage-table-usage-sep"> / </span>
    <span className="usage-table-total">{total}</span>
  </>
);

const UsageTable: React.FC<UsageTableProps> = ({ rows }) => (
  <>
    <table className="usage-table usage-table-desktop">
      <thead>
        <tr>
          <th>Email ID</th>
          <th>Region</th>
          <th>Weekly Queries</th>
          <th>Total Queries</th>
          <th>Weekly Messages</th>
          <th>Total Messages</th>
          <th>Shares (Last 30 Days)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          // Zero-activity rows render at full opacity — a non-adopter is
          // the most important row on this page, never hidden or dimmed.
          <tr key={row.account_id}>
            <td>
              <div className="usage-table-identity">
                <Avatar email={emailLabel(row)} />
                <span className="usage-table-email">{emailLabel(row)}</span>
              </div>
            </td>
            <td><RegionPill region={row.region} /></td>
            <td className="usage-table-usage-number">{row.last_7_days.chats_started}</td>
            <td className="usage-table-total">{row.till_date.chats_started}</td>
            <td>
              <div className="usage-table-usage-cell">
                <span className="usage-table-usage-number">{row.last_7_days.turns}</span>
                <TrendLabel cur={row.last_7_days.turns} prev={row.prev_7_days.turns} activeDays={row.last_7_days.active_days} />
              </div>
            </td>
            <td className="usage-table-total">{row.till_date.turns}</td>
            <td className="usage-table-usage-number">{row.shares_last_30_days}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <div className="usage-table-mobile">
      {rows.map((row) => (
        <div key={row.account_id} className="usage-card">
          <div className="usage-card-header">
            <Avatar email={emailLabel(row)} />
            <span className="usage-table-email">{emailLabel(row)}</span>
          </div>
          <RegionPill region={row.region} />
          <div className="usage-card-numbers">
            <div className="usage-card-metric">
              <span className="usage-card-metric-label">Queries</span>
              <span><MetricPair weekly={row.last_7_days.chats_started} total={row.till_date.chats_started} /></span>
            </div>
            <div className="usage-card-metric">
              <span className="usage-card-metric-label">Messages</span>
              <span><MetricPair weekly={row.last_7_days.turns} total={row.till_date.turns} /></span>
              <TrendLabel cur={row.last_7_days.turns} prev={row.prev_7_days.turns} activeDays={row.last_7_days.active_days} />
            </div>
            <div className="usage-card-metric">
              <span className="usage-card-metric-label">Shares (30d)</span>
              <span className="usage-table-usage-number">{row.shares_last_30_days}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </>
);

export default UsageTable;
